<template>
    <div class="command">
        <h1>Vos commandes</h1>
        <div class="nextDelivery mr-4">
            <div class="flex content-center mb-2">
                <h4 class="mr-4">Votre prochaine livraison:</h4>
                <p v-if="nextDelivery">{{ nextDelivery.date | moment }}</p>
            </div>
            <p class="mb-0 mr-24 edit">
                Vous pouvez modifier la date de livraison et/ou la quantité livrée en
                cliquant sur le produit concerné dans le calendrier ci-dessous.
            </p>
            <br>
            <div class="mb-4">
                <p class="mt-0 offers-link">
                    Vous souhaitez en savoir plus sur nos offres ? Rendez-vous juste
                    <router-link :to="{ name: 'offers' }"> ici</router-link>
                    !
                </p>
            </div>

            <div class="mb-4">
                <div class="mb-2">
                    <h4>Adresses de livraison:</h4>
                </div>
                <div v-for="address in addresses" :key="address.id" class="mb-2">
                    <b-form-checkbox v-model="selectedAddressesIds" :value="address.id" @change="refetch">
                        {{ address.name }}, {{ address.address }}, {{ address.zip }}, {{ address.city }}
                    </b-form-checkbox>
                </div>
            </div>

            <div class="calendarContainer" style="position: relative;">
                <FullCalendar ref="fullCalendar" :options="calendarOptions" style="max-width: 1700px">
                    <template v-slot:eventContent="arg">
                        <Event :refetch-function="refetch" :arg="arg"/>
                    </template>
                </FullCalendar>
                <div v-if="isLoading"
                     style="position: absolute; top: 0; right: 0; height: 100%; width: 100%; background-color: white; opacity: 0.5; z-index: 50;">
                    <b-spinner style="position: absolute; top: 25%; left: calc(50% - 15px); width: 30px; height: 30px;"
                               label="Loading..."></b-spinner>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import {notifyErrorFetchRaw} from "@/helper";
import {api, API_ROUTES} from "@/Services/api.common.service";
import {
    convertAllCategoriesToBigCat,
    set00h00AllElementAndSetStrCat,
    sortByCategories,
    sortByDateElement,
    getAddressAndDate,
} from "@/views/Commands/command.functions";
import Event from "@/views/Commands/Event";
import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import Moment from "moment";
import _ from "lodash";

export default {
    components: {
        FullCalendar,
        Event,
    },
    data() {
        return {
            isLoading: true,
            events: [],
            nextDelivery: undefined,
            categoriesToDisplay: [],
            calendarOptions: {
                height: "80vh",
                dayMinWidth: "400px",
                plugins: [dayGridPlugin, interactionPlugin, listPlugin],
                headerToolbar: {
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridWeek,dayGridMonth",
                },
                titleRangeSeparator: " au ",
                titleFormat: {month: "long", day: "numeric", year: "numeric"},
                buttonText: {
                    week: "Par semaine",
                    month: "Par mois",
                },
                initialView: "dayGridWeek",
                hiddenDays: [0],
                locales: [frLocale],
                showNonCurrentDates: false,
                fixedWeekCount: false,
                events: this.eventsFunction,
            },
            addresses: [],
            selectedAddressesIds: undefined,
        };
    },
    watch: {
        $route: "fetchData",
    },
    created() {
        this.fetchData();
        this.fetchAddresses();
    },
    methods: {
        refetch() {
            const api = this.$refs.fullCalendar.getApi();
            api.refetchEvents();
        },
        async eventsFunction(info) {
            if (this.selectedAddressesIds === undefined) {
                return [];
            }

            this.isLoading = true

            const notifyError = notifyErrorFetchRaw.bind(this);

            const start = new Moment(new Date(info.start));
            const end = new Moment(new Date(info.end));

            try {
                const resApi = await api().get(
                    API_ROUTES.commands.commands +
                    "/" +
                    start.format("yyyy-MM-DD") +
                    "/" +
                    end.format("yyyy-MM-DD") +
                    "?addressesIds=" + JSON.stringify(this.selectedAddressesIds)
                );

                const eventApi = resApi.data;
                const closeDateStart = Moment('29/07/2024', 'DD/MM/YYYY');
                const closeDateEnd = Moment('18/08/2024', 'DD/MM/YYYY');
                eventApi.commands = eventApi.commands.filter(command => !Moment(command.date).isBetween(closeDateStart, closeDateEnd, 'day', '[]'));

                this.getAllCat(eventApi?.commands);
                const eventApiBigCat = convertAllCategoriesToBigCat(
                    eventApi?.commands || []
                );

                const sameHourMin = eventApiBigCat.map(set00h00AllElementAndSetStrCat);
                const sortedByDate = sortByDateElement(sameHourMin);

                const sortByCat = sortByCategories(sortedByDate);

                const addressAndDate = getAddressAndDate(sortedByDate);
                const merged = _.merge([...sortByCat], addressAndDate);

                const breaksApi = eventApi?.breaks;

                if (breaksApi.length) {
                    breaksApi.forEach((commandBreak) => {
                        merged.push(this.createBreakEvent(
                            commandBreak.startDate,
                            commandBreak.endDate,
                            commandBreak.address,
                        ));
                    });
                }

                this.isLoading = false

                merged.push({
                    start: closeDateStart.format('YYYY-MM-DDTHH:mm:ss.000')+'Z',
                    end: closeDateEnd.format('YYYY-MM-DDTHH:mm:ss.000')+'Z',
                    type: 'deliclosed',
                    allDay: true,
                    backgroundColor: "#D3305F",
                    borderColor: "#D3305F",
                    textColor: '#000'
                });

                return merged;
            } catch (e) {
                console.error(e);
                notifyError();
            }

            this.isLoading = false;

            return [];
        },
        async fetchData() {
            const notifyError = notifyErrorFetchRaw.bind(this);

            try {
                const resApi = await api().get(API_ROUTES.commands.nextDelivery);
                this.nextDelivery = resApi?.data?.nextDelivery;
            } catch (e) {
                console.error(e);
                notifyError();
            }
        },
        getAllCat(allEvents) {
            try {
                const catInsideCommand = {};
                allEvents.forEach(
                    (event) => (catInsideCommand[event.categoryID] = true)
                );
                this.categoriesToDisplay = Object.keys(catInsideCommand);
            } catch (e) {
                console.error(e)
            }
        },
        async fetchAddresses() {
            const notifyError = notifyErrorFetchRaw.bind(this);

            try {
                const resApi = await api().get(API_ROUTES.information.deliveriesAddresses);
                this.addresses = resApi?.data?.addresses;
                this.selectedAddressesIds = this.addresses.map((address) => address.id);
                this.refetch();
            } catch (e) {
                console.error(e);
                notifyError();
            }
        },
        createBreakEvent(startDate, endDate, address) {
            return {
                start: startDate,
                end: endDate,
                type: 'break',
                address: address,
                dateEnd: endDate,
                allDay: true,
                backgroundColor: "#D3305F",
                borderColor: "#D3305F",
                textColor: '#000'
            }
        }
    },
};
</script>

<style lang="scss">
.command {
    padding-right: 0;

    h1 {
        font-size: 3.5em;
        margin-bottom: 40px;
    }

    .offers-link a {
        font-weight: bold;
        text-decoration: underline;
    }

}

.nextDelivery {
    h4 {
        font-size: 18px;
        line-height: 20px;
    }

    p {
        font-weight: 500;
        line-height: 20px;
        margin-top: 1px;
    }

    .edit {
        font-size: 0.9em;
    }
}

.fc-prev-button,
.fc-next-button {
    display: flex !important;
    align-content: center !important;

    span {
        vertical-align: unset !important;
        font-size: 1.5em !important;
    }
}
</style>
